/**
 * IdleLogoutGuard — global inactivity auto-logout.
 *
 * Reads the Super-Admin-configured policy from /system/portal-status
 * (idle_logout_enabled / idle_logout_minutes / idle_logout_warning_seconds).
 * Super Admin is exempt (the backend returns enabled=false for them). Tracks
 * user activity (mouse/keyboard/scroll/touch), syncs "last activity" across
 * tabs via localStorage, shows a warning countdown dialog, and logs the user
 * out when the idle window elapses.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const LS_KEY = 'inv_last_activity';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'wheel'];

export default function IdleLogoutGuard() {
  const { user, logout } = useAuth();
  const [policy, setPolicy] = useState({ enabled: false, idleMs: 0, warnMs: 60000 });
  const [warnOpen, setWarnOpen] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const lastActivityRef = useRef(Date.now());
  const lastWriteRef = useRef(0);
  const tickRef = useRef(null);

  // Load the policy whenever the user changes (login).
  useEffect(() => {
    let cancelled = false;
    if (!user) { setPolicy({ enabled: false, idleMs: 0, warnMs: 60000 }); return; }
    (async () => {
      try {
        const { data } = await api.get('/system/portal-status');
        if (cancelled) return;
        const minutes = Number(data.idle_logout_minutes || 30);
        const warnSec = Number(data.idle_logout_warning_seconds || 60);
        setPolicy({
          enabled: !!data.idle_logout_enabled,
          idleMs: Math.max(60_000, minutes * 60_000),
          warnMs: Math.min(Math.max(10_000, warnSec * 1000), Math.max(10_000, minutes * 60_000 - 5000)),
        });
        lastActivityRef.current = Date.now();
      } catch {
        if (!cancelled) setPolicy({ enabled: false, idleMs: 0, warnMs: 60000 });
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const markActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    // Throttle cross-tab writes to once every 4s.
    if (now - lastWriteRef.current > 4000) {
      lastWriteRef.current = now;
      try { localStorage.setItem(LS_KEY, String(now)); } catch {}
    }
  }, []);

  const staySignedIn = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    try { localStorage.setItem(LS_KEY, String(now)); } catch {}
    setWarnOpen(false);
  }, []);

  useEffect(() => {
    if (!user || !policy.enabled || !policy.idleMs) {
      setWarnOpen(false);
      return undefined;
    }
    // Activity listeners.
    const onActivity = () => markActivity();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    // Cross-tab sync: adopt the freshest activity time from any tab.
    const onStorage = (e) => {
      if (e.key === LS_KEY && e.newValue) {
        const v = Number(e.newValue);
        if (!Number.isNaN(v)) lastActivityRef.current = Math.max(lastActivityRef.current, v);
      }
    };
    window.addEventListener('storage', onStorage);

    // Seed shared timestamp.
    try {
      const existing = Number(localStorage.getItem(LS_KEY) || '0');
      if (existing && Date.now() - existing < policy.idleMs) {
        lastActivityRef.current = existing;
      } else {
        localStorage.setItem(LS_KEY, String(Date.now()));
      }
    } catch {}

    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = policy.idleMs - elapsed;
      if (remaining <= 0) {
        clearInterval(tickRef.current);
        setWarnOpen(false);
        try { localStorage.setItem('inv_idle_logout', '1'); } catch {}
        logout();
      } else if (remaining <= policy.warnMs) {
        setWarnOpen(true);
        setCountdown(Math.ceil(remaining / 1000));
      } else if (warnOpen) {
        setWarnOpen(false);
      }
    }, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      window.removeEventListener('storage', onStorage);
      if (tickRef.current) clearInterval(tickRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, policy.enabled, policy.idleMs, policy.warnMs, markActivity, logout]);

  if (!user || !policy.enabled) return null;

  return (
    <Dialog open={warnOpen} onOpenChange={(v) => { if (!v) staySignedIn(); }}>
      <DialogContent className="max-w-sm" data-testid="idle-logout-warning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock size={18} className="text-[var(--ink-navy)]" /> Are you still there?
          </DialogTitle>
          <DialogDescription>
            You’ve been inactive for a while. For security you’ll be signed out in{' '}
            <span className="font-semibold text-[var(--ink-navy)] font-mono tabular-nums" data-testid="idle-logout-countdown">{countdown}</span>{' '}
            second{countdown === 1 ? '' : 's'}.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => { try { localStorage.setItem('inv_idle_logout', '1'); } catch {} logout(); }} data-testid="idle-logout-now-button">
            Log out now
          </Button>
          <Button className="luxury-button" onClick={staySignedIn} data-testid="idle-logout-stay-button">
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
